import { Editor, Element, Node, NodeEntry, Path, Transforms } from 'slate';
import { getBlockTagBindings, isBlockTagCarrierType, TagBinding } from '../types/block';

/**
 * Locate the block-tag carrier whose `tags[]` binding holds `tagId`, regardless
 * of tag metadata. Returns null when the tag is not a block binding (e.g. an
 * inline prose tag, or absent). Lets delete flows detect block tags by id so a
 * by-id delete can never remove the DB row while leaving the Slate binding.
 */
export function findBlockCarrierForTag(editor: Editor, tagId: string): NodeEntry | null {
  const entry = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && isBlockTagCarrierType((n as { type?: string }).type) && getBlockTagBindings(n).some((b) => b.tagId === tagId),
    })
  )[0];
  return entry ?? null;
}

export function applyBlockTagBinding(editor: Editor, path: Path, binding: TagBinding): void {
  const node = Node.get(editor, path);
  const existing = getBlockTagBindings(node);
  const next = [...existing, binding];
  Transforms.setNodes(editor, { tags: next } as Partial<Node>, { at: path });
}

/**
 * Remove a block-tag binding from the carrier at `path` and repair the
 * continuation chain: any carrier whose binding pointed at `tagId` is
 * re-stitched to the removed binding's successor (or has its `continuedAt`
 * cleared if the removed binding was the tail). Mirrors the inline `removeTag`
 * relink and the backend `nextSegment` repair so the Slate chain never dangles.
 */
export function removeBlockTagBinding(editor: Editor, path: Path, tagId: string): void {
  const node = Node.get(editor, path);
  const removed = getBlockTagBindings(node).find((b) => b.tagId === tagId);
  const successorId = removed?.continuedAt;

  relinkBlockPredecessor(editor, tagId, successorId);

  const remaining = getBlockTagBindings(node).filter((b) => b.tagId !== tagId);
  if (remaining.length === 0) {
    Transforms.unsetNodes(editor, 'tags', { at: path });
  } else {
    Transforms.setNodes(editor, { tags: remaining } as Partial<Node>, { at: path });
  }
}

/** Re-point (or clear) the binding on whichever carrier continues into `tagId`. */
function relinkBlockPredecessor(editor: Editor, tagId: string, successorId: string | undefined): void {
  const predecessor = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && isBlockTagCarrierType((n as { type?: string }).type) && getBlockTagBindings(n).some((b) => b.continuedAt === tagId),
    })
  )[0];
  if (!predecessor) return;

  const [predNode, predPath] = predecessor;
  const relinked = getBlockTagBindings(predNode).map((b) => {
    if (b.continuedAt !== tagId) return b;
    if (successorId) return { ...b, continuedAt: successorId };
    const { continuedAt, ...rest } = b;
    return rest;
  });
  Transforms.setNodes(editor, { tags: relinked } as Partial<Node>, { at: predPath });
}
